import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigurationSection, SpaceBetweenRow } from '../../StyleComponents';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { Switch } from '@shared/ui/atoms/Switch';
import TextField from '@shared/ui/atoms/TextField';
import { Row } from '@shared/ui/atoms/StructuralLayout';
import { selectConfig, selectIsConfigLoading, updateConfig } from '@shared/store/config.duck';

export interface HidePastWhereaboutsSettingHandle {
  save: () => Promise<void>;
}

export const HidePastWhereaboutsSetting = forwardRef<HidePastWhereaboutsSettingHandle, {}>((props, ref) => {
  const dispatch = useDispatch();
  const config = useSelector(selectConfig);
  const isLoading = useSelector(selectIsConfigLoading);
  const [enabled, setEnabled] = useState(false);
  const [weeksLimit, setWeeksLimit] = useState(4);

  // Depend on the two fields rather than the config object so unrelated
  // config updates don't clobber in-progress edits.
  useEffect(() => {
    setEnabled(!!config?.hidePastWhereaboutsEnabled);
    setWeeksLimit(config?.hidePastWhereaboutsWeeksLimit ?? 4);
  }, [config?.hidePastWhereaboutsEnabled, config?.hidePastWhereaboutsWeeksLimit]);

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!config) {
        return;
      }
      const action = await dispatch(updateConfig({
        ...config,
        hidePastWhereaboutsEnabled: enabled,
        hidePastWhereaboutsWeeksLimit: weeksLimit,
      }));
      if ((action as any)?.meta?.requestStatus === 'rejected') {
        throw new Error('Failed to save past whereabouts visibility');
      }
    },
  }), [config, enabled, weeksLimit, dispatch]);

  return (
    <ConfigurationSection>
      <SpaceBetweenRow style={{ marginBottom: 2 }}>
        <BodyLarge colour={Colours.blue}>Hide past whereabouts</BodyLarge>
        <Switch value={enabled} label="" disabled={isLoading} onChange={setEnabled} />
      </SpaceBetweenRow>
      <BodyRegular style={{ marginBottom: 16 }}>
        Restrict everyone except company admins from seeing past whereabouts. The current week is always
        visible, plus the number of previous weeks below.
      </BodyRegular>

      <Row>
        <BodyRegular weight={600} style={{ marginTop: 6, marginRight: 16 }}>Number of previous weeks visible</BodyRegular>
        <TextField onChange={(val: string) => setWeeksLimit(Math.max(0, parseInt(val) || 0))}
                   type={'number'}
                   disabled={isLoading || !enabled}
                   value={weeksLimit.toString()}
                   style={{ width: 60, marginBottom: 0 }} />
      </Row>
    </ConfigurationSection>
  );
});

HidePastWhereaboutsSetting.displayName = 'HidePastWhereaboutsSetting';
